<template>
  <div id="app">
    <div class="row">
      <div class="col-12">
        <h2 ref="productsTitle" class="generalTitle">Productos</h2>
      </div>
    </div>
    <div class="row mx-1">
      <div class="col-12 mb-3">
        <list-filters @filter="debouncedFilter" />
      </div>

      <!-- Barra de progreso, solo para la carga inicial -->
      <div v-if="loading && !loadingPagination" class="col-12 mb-3">
        <div class="progress-bar-container">
          <div class="progress-bar"></div>
        </div>
      </div>

      <!-- Productos list -->
      <div class="col-12" v-if="!loading && products && products.length > 0">
        <div class="card" :class="{ 'card-versionMovil': !$store.state.responsive.viewDesktopLists}">
          <div class="card-body pb-0">
            <div class="align-items-center d-flex justify-content-between mb-1">
              <p class="mb-2">Mostrando {{ from }}-{{ to }} de {{ totalItems }} productos</p>
              <a href="#" class="btn btn-sm btn-outline-primary" @click.prevent="downloadReferences" :disabled="downloadingCSV">
                <span v-if="downloadingCSV" class="spinner-border spinner-border-sm mr-1" role="status"></span>
                <v-icon v-else name="bi-download" scale="0.9" class="mr-1" />
                DESCARGAR REFERENCIAS
              </a>
            </div>
            <div v-if="downloadMessage" class="alert alert-success alert-dismissible py-2 mb-2">
              {{ downloadMessage }}
              <button type="button" class="close py-2" @click="downloadMessage = ''"><span>&times;</span></button>
            </div>
            <div v-if="downloadError" class="alert alert-danger alert-dismissible py-2 mb-2">
              {{ downloadError }}
              <button type="button" class="close py-2" @click="downloadError = ''"><span>&times;</span></button>
            </div>
            <list :products="products" @selectOrder="selectOrder($event)" />
            <!-- Spinner de precarga al cambiar número de productos -->
            <div v-if="loadingPagination" class="text-center mt-3 mb-3">
              <div class="spinner-border" role="status">
                <span class="sr-only">Cargando...</span>
              </div>
            </div>
            <div class="row">
              <div class="col-12 col-sm-8 col-lg-5">
                <div class="form-group" v-if="numberPages > 1">
                  <div class="float-left mr-3 mt-2">
                    <select v-model="itemsPerPage" class="form-control" id="itemsPerPage" @change="changeItemsPerPage">
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  <label for="itemsPerPage" class="col-form-label mt-2">Productos por página</label>
                </div>
              </div>
              <div class="col-12 col-sm-4 col-lg-7" v-if="numberPages > 1">
                <pagination :currentPage="currentPage" :numberPages="numberPages" @selectPage="selectPage" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center" v-if="!loading && (!products || products.length === 0)">
        <div class="alert alert-info">No hay resultados para mostrar</div>
      </div>
    </div>
    <ExportData :id="'exportData'" :modalTitle="'EXPORTAR DATOS'" :class="'modal-xl'" :showFooter="false"
                :filters="filters" :order="order"></ExportData>
  </div>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';
import { getProductUrl } from '@/repositories/apiCSV.js';
import List from './templates/List.vue';
import ListFilters from '@/components/Widgets/Shared/ProductListFilters.vue';
import Pagination from '@/components/Widgets/Shared/Pagination.vue';
import ExportData from './templates/ExportData.vue';
import eventBus from '@/components/Widgets/Shared/eventBus.js';

export default {
  name: 'App',
  data() {
    return {
      loading: true,
      loadingPagination: false,
      brand: '',
      model: '',
      capacity: '',
      color: '',
      stock: '',
      priceTo: '',
      priceFrom: '',
      products: [],
      filters: '',
      itemsPerPage: 25,
      currentPage: 0,
      totalItems: 0,
      numberPages: 0,
      from: 0,
      to: 0,
      order: '',
      downloadingCSV: false,
      downloadMessage: '',
      downloadError: '',
    };
  },
  components: {
    List,
    ListFilters,
    Pagination,
    ExportData,
  },
  created() {
    this.loadProducts();
    this.debouncedFilter = debounce(this.filter, 300); // Implementación de debounce
  },
  methods: {
    generateFilters() {
      const params = new URLSearchParams();

      if (this.brand) params.append('brand', this.brand);
      if (this.model) params.append('model', this.model);
      if (this.color) params.append('color', this.color);
      if (this.capacity) params.append('capacity', this.capacity);
      if (this.stock) params.append('inStock', this.stock);
      if (this.priceFrom) params.append('priceFrom', this.priceFrom);
      if (this.priceTo) params.append('priceTo', this.priceTo);

      params.append('pagination.pageSize', this.itemsPerPage);
      params.append('pagination.page', this.currentPage + 1);

      return `?${params.toString()}`;
    },
    filter(value) {
      this.brand = value.brand !== undefined ? value.brand : this.brand;
      this.model = value.model !== undefined ? value.model : this.model;
      this.capacity = value.capacity !== undefined ? value.capacity : this.capacity;
      this.color = value.color !== undefined ? value.color : this.color;
      this.stock = value.stock !== undefined ? value.stock : this.stock;
      this.priceFrom = value.priceFrom !== undefined ? value.priceFrom : this.priceFrom;
      this.priceTo = value.priceTo !== undefined ? value.priceTo : this.priceTo;
      this.currentPage = 0;
      this.loadProducts();
    },
    filterBrand() {
      if (this.brand !== '' && this.brand !== undefined) {
        this.filters = `${this.filters}brand=${encodeURIComponent(this.brand)}&`;
      }
    },
    filterModel() {
      if (this.model !== '' && this.model !== undefined) {
        this.filters = `${this.filters}model=${encodeURIComponent(this.model)}&`;
      }
    },
    filterColor() {
      if (this.color !== '' && this.color !== undefined) {
        this.filters = `${this.filters}color=${encodeURIComponent(this.color)}&`;
      }
    },
    filterCapacity() {
      if (this.capacity !== '' && this.capacity !== undefined) {
        this.filters = `${this.filters}capacity=${encodeURIComponent(this.capacity)}&`;
      }
    },
    filterStock() {
      if (this.stock !== '' && this.stock !== undefined) {
        this.filters = `${this.filters}inStock=${encodeURIComponent(this.stock)}&`;
      }
    },
    filterPrice() {
      if (this.priceFrom !== '' && this.priceFrom !== undefined) {
        this.filters = `${this.filters}priceFrom=${encodeURIComponent(this.priceFrom)}&`;
      }
      if (this.priceTo !== '' && this.priceTo !== undefined) {
        this.filters = `${this.filters}priceTo=${encodeURIComponent(this.priceTo)}&`;
      }
    },
    filterPagination() {
      this.filters = `${this.filters}pagination.pageSize=${this.itemsPerPage}&`;
      this.filters = `${this.filters}pagination.page=${parseInt(this.currentPage + 1)}&`;
    },
    selectPage(value) {
      this.currentPage = value;
      this.scrollToTop(); // Subir al top cuando se cambia la página
      this.loadProducts();
    },
    scrollToTop() {
      const element = this.$refs.productsTitle;
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    changeItemsPerPage() {
      this.loadingPagination = true; // Spinner para cambio de número de productos
      this.currentPage = 0;
      this.loadProducts();
    },
    downloadReferences() {
      this.downloadingCSV = true;
      this.downloadMessage = '';
      this.downloadError = '';

      const filters = {
        brand: this.brand || '',
        model: this.model || '',
        color: this.color || '',
        capacity: this.capacity || '',
      };

      getProductUrl(filters)
        .then((res) => {
          if (res.data && res.data.data && res.data.data.productExport && res.data.data.productExport.length > 0) {
            window.location.href = res.data.data.productExport[0].downloadLink;
            this.downloadMessage = 'Descarga iniciada correctamente.';
          } else {
            this.downloadError = 'No se encontraron datos para exportar.';
          }
        })
        .catch((error) => {
          console.error('Error al descargar referencias:', error);
          this.downloadError = 'Error al generar el CSV. Inténtalo de nuevo.';
        })
        .finally(() => {
          this.downloadingCSV = false;
        });
    },
    loadProducts() {
      this.filters = '';
      this.filterBrand();
      this.filterModel();
      this.filterColor();
      this.filterCapacity();
      this.filterStock();
      this.filterPrice();
      this.filterPagination();

      if (!this.loadingPagination) {
        this.loading = true;
      }

      const filters = this.generateFilters(); // Asegúrate de que la función existe

      axios.get(`/products/search${filters}`)
          .then((response) => {
            this.loading = false;
            this.loadingPagination = false;

            if (response.status === 200) {
              this.products = response.data.products;
              this.totalItems = response.data.pagination.totalElements;
              this.numberPages = this.totalItems % this.itemsPerPage === 0
                  ? this.totalItems / this.itemsPerPage
                  : Math.ceil(this.totalItems / this.itemsPerPage);

              this.from = (this.currentPage * this.itemsPerPage) + 1;
              this.to = Math.min((this.currentPage * this.itemsPerPage) + this.itemsPerPage, this.totalItems);
              
              // Emitir evento de carga completada
              eventBus.emit("productsLoaded");
            }
          })
          .catch((error) => {
            this.loading = false;
            this.loadingPagination = false;
            
            // Emitir evento de error
            eventBus.emit("productsError");

            if (error.response) {
              console.error(`Error: ${error.response.status}, ${error.response.data.message || 'Unknown error'}`);
              this.showErrorMessage("Error al cargar los productos.");
            } else if (error.request) {
              console.error("Error de conexión con el servidor.");
              this.showErrorMessage("No se pudo contactar con el servidor.");
            } else {
              console.error("Error en la solicitud:", error.message);
              this.showErrorMessage("Error inesperado. Inténtalo más tarde.");
            }
          });
    }
  },
};
</script>

<style lang="scss" scoped>
.dropzone-container {
  max-height: unset;
}

.progress-bar-container {
  width: 100%;
  height: 4px;
  background-color: #f3f3f3;
  position: relative;
  margin-bottom: 10px;
}

.progress-bar {
  width: 0;
  height: 100%;
  background-color: #97055D;
  animation: loading 2s infinite;
}

@keyframes loading {
  0% {
    width: 0;
  }
  50% {
    width: 50%;
  }
  100% {
    width: 100%;
  }
}

.spinner-border {
  width: 2rem;
  height: 2rem;
  border-width: 0.25rem;
  color: #97055D;
}
</style>
